import * as React from 'react';

import { ElementInViewport } from '../../../common/components/elementInViewport';
import { SimilarOffers } from '../../../components/SimilarOffersRedesigned';
import {
  EtrackOfferClicksLabels,
  EtrackOffersShowActions,
  trackOffersShow,
  TProductPartial,
} from '../../../ebc/track_similar';
import { EModelTypes, ISimilarState } from '../../../store/similar';
import { IOfferData } from '../../../types/offerData';
import { getVariants } from '../../../utils/ebc';
import { getPageEBC } from '../../../utils/tracking/get_page_ebc';
import { getUserEBC } from '../../../utils/tracking/get_user_ebc';

interface ISimilarOffersFactoryParams {
  offerData: IOfferData;
  withoutCompetitors: boolean;
  similarOffers: ISimilarState;
  abGroup: number;
}

export function SimilarOffersFactory({
  offerData,
  withoutCompetitors,
  similarOffers,
  abGroup,
}: ISimilarOffersFactoryParams) {
  const user = getUserEBC(offerData.user, abGroup);
  const page = getPageEBC(offerData.offer.id, offerData.breadcrumbs);

  const SimilarOffersWrapper = ({
    modelType,
    title,
    disableExternalContainerStyles,
  }: {
    modelType: EModelTypes;
    title?: string | React.ReactNode;
    disableExternalContainerStyles?: boolean;
  }) => {
    const modelVersion = similarOffers[modelType].modelVersion;
    const similarOffersList = similarOffers[modelType].list;
    const [wasShown, setWasShown] = React.useState(false);

    const products = React.useMemo(
      () =>
        similarOffersList.map((similarOffer, index): TProductPartial => {
          let variant;

          /* istanbul ignore else */
          if (similarOffer.gaVariant) {
            variant = getVariants(similarOffer.gaVariant);
          }

          return {
            id: similarOffer.cianId,
            position: index + 1,
            ownerId: similarOffer.publishedUserId || undefined,
            variant,
          };
        }),
      [similarOffersList],
    );

    const onVisibilityChange = React.useCallback(
      (isVisible: boolean) => {
        const eventAction = withoutCompetitors
          ? EtrackOffersShowActions.show_others_offers
          : EtrackOffersShowActions.show_similar_offers;

        /* istanbul ignore else */
        if (isVisible && !wasShown && products.length) {
          setWasShown(true);

          trackOffersShow({
            action: eventAction,
            label:
              !withoutCompetitors || modelType === EModelTypes.HistoryBased
                ? EtrackOfferClicksLabels[modelType]
                : undefined,
            products,
            modelVersion,
            user,
            page,
          });
        }
      },
      [modelVersion, modelType, wasShown, products],
    );

    return (
      <ElementInViewport onChange={onVisibilityChange} viewPercent={0.7}>
        <SimilarOffers
          agent={offerData.agent}
          offer={offerData.offer}
          similarOffers={similarOffersList}
          withoutCompetitors={withoutCompetitors}
          modelVersion={modelVersion}
          modelType={modelType}
          user={user}
          page={page}
          title={title}
          disableExternalContainerStyles={disableExternalContainerStyles}
        />
      </ElementInViewport>
    );
  };

  return SimilarOffersWrapper;
}
